/**
 * Presentation-only helper: groups bill_lines rows that represent the same
 * SOV item across multiple units into a single printable row.
 *
 * Stored data is NOT modified — this only affects the printed/PDF bill.
 *
 * Grouping key: (base description with " — Unit NNN" stripped, unit_price).
 * Quantities and line_totals are summed using already-rounded stored values
 * so the printed subtotal matches the bill header exactly.
 */

export interface RawBillLine {
  description: string | null;
  quantity: number | string | null;
  unit_price: number | string | null;
  line_total: number | string | null;
}

export interface PrintableBillLine {
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** Strip a trailing " — Unit NNN" / " - Unit NNN" suffix from a description. */
const stripUnitSuffix = (desc: string): string =>
  desc.replace(/\s*[—\-–]\s*Unit\s+\S+\s*$/i, "").trim();

export function groupBillLinesForPrint(rows: RawBillLine[]): PrintableBillLine[] {
  const order: string[] = [];
  const groups = new Map<string, PrintableBillLine>();

  for (const row of rows ?? []) {
    const rawDesc = (row.description ?? "").toString();
    const baseDesc = stripUnitSuffix(rawDesc) || rawDesc;
    const qty = Number(row.quantity ?? 0);
    const price = Number(row.unit_price ?? 0);
    const total = Number(row.line_total ?? 0);

    const key = `${baseDesc}::${price.toFixed(4)}`;

    const existing = groups.get(key);
    if (existing) {
      existing.quantity = r2(existing.quantity + qty);
      existing.line_total = r2(existing.line_total + total);
    } else {
      order.push(key);
      groups.set(key, {
        description: baseDesc,
        quantity: r2(qty),
        unit_price: r2(price),
        line_total: r2(total),
      });
    }
  }

  return order.map((k) => groups.get(k)!);
}
